import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import styled, { css } from "styled-components";

import { CloseIcon } from "../../icons/glyphs";
import { useFieldControl } from "../Field/FieldContext";
import { IconButton } from "../IconButton";
import { controlFocusRing, controlRadius } from "../foundations/controlStyles";
import { usePopoverDismiss } from "../foundations/popoverDismiss";
import type { InputSize } from "../Input/foundations/inputTypes";
import {
  Accessories,
  Control,
  EmptyText,
  Input,
  Menu,
  Option,
  OptionDescription,
  OptionLabel,
  OptionMain,
  Shell,
  defaultMaxVisibleOptions,
  filterOptions,
  moveActiveIndex,
  type SearchOption,
} from "./searchComboboxShared";

/** 선택된 검색 토큰(칩). `accentColor`는 칩 좌측 막대 + 옵션 dot 색으로 쓰인다. */
export type SearchToken = {
  value: string;
  label: string;
  description?: string;
  accentColor?: string;
};

/** `renderResult` 커스텀 렌더에 주입되는 헬퍼. */
export type SearchResultHelpers = {
  /** 이 토큰을 선택에서 제거. */
  remove: () => void;
  /** 입력이 disabled 상태인지. */
  disabled: boolean;
};

export interface TokenSearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "size" | "disabled"
> {
  /** 자유 텍스트 입력값(부모 소유). 서버 질의·debounce는 호출자 책임. */
  inputValue: string;
  /** 선택된 토큰(부모 소유). */
  selectedTokens: readonly SearchToken[];
  /** 자동완성 후보. */
  options: readonly SearchOption[];
  onInputValueChange: (value: string) => void;
  onSelectedTokensChange: (tokens: readonly SearchToken[]) => void;
  /** "sm" | "md". 기본 "md". c6 InputSize와 통일. */
  inputSize?: InputSize;
  /** 컨트롤과 펼친 옵션 메뉴의 폭. 숫자는 px, 문자열은 CSS 길이. 미지정 시 부모 폭 100%. */
  width?: number | string;
  invalid?: boolean;
  disabled?: boolean;
  /** 매칭 후보가 없을 때 문구. `renderOnEmpty` 가 없을 때만 쓰인다. */
  emptyMessage?: string;
  /** 매칭 후보가 0개일 때 메뉴에 렌더할 커스텀 노드. 현재 입력어를 인자로 받는다. */
  renderOnEmpty?: (query: string) => ReactNode;
  /** 선택된 결과(토큰)를 칩 대신 커스텀 렌더. 토큰 1개씩 호출되며 미지정 시 기본 칩. */
  renderResult?: (token: SearchToken, helpers: SearchResultHelpers) => ReactNode;
  /** 메뉴에 한 번에 보이는 후보 상한. 기본 8. */
  maxVisibleOptions?: number;
  /** 입력 안쪽 오른쪽 끝의 액세서리(예: 단위 텍스트 `SearchInputAccessory`). clear 버튼 왼쪽에 놓인다. */
  accessory?: ReactNode;
  /** 입력값/토큰이 있을 때 안쪽 오른쪽 끝에 내부 관리 clear 버튼을 노출(입력 + 토큰 전체 삭제). */
  showClear?: boolean;
  /** 매칭 키워드 커스터마이즈. 기본은 label/value/description. */
  getOptionKeywords?: (option: SearchOption) => readonly string[];
}

const defaultEmptyMessage = "No matching options.";

const Token = styled.span<{ $accentColor?: string }>`
  display: inline-flex;
  max-width: min(18rem, 100%);
  min-width: 0;
  align-items: center;
  gap: 0.24rem;
  padding: 0.1rem 0.24rem;
  border: 1px solid ${({ theme }) => theme.color.border.selected};
  ${({ $accentColor }) =>
    $accentColor &&
    css`
      padding-left: 0.5rem;
      border-left: 0.2rem solid ${$accentColor};
    `}
  ${controlRadius}
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
  color: ${({ theme }) => theme.color.text.default};
  font-weight: ${({ theme }) => theme.typography.weight.extrabold};
  line-height: 1;
`;

const TokenLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  ${controlRadius}
  background: transparent;
  color: ${({ theme }) => theme.color.text.subtle};
  cursor: pointer;
  font: inherit;
  line-height: 1;

  &:hover {
    background: ${({ theme }) => theme.color.bg.surfaceRaised};
    color: ${({ theme }) => theme.color.text.default};
  }

  &:focus-visible {
    ${controlFocusRing("1px")}
  }
`;

/**
 * 토큰(칩) 자동완성 검색 입력(콤보박스). 자유 텍스트로 후보를 필터하고 Enter/클릭으로 토큰(칩)을
 * 추가, Backspace로 마지막 토큰을 제거한다. 자유 검색어 자체는 소유하지 않고 `onInputValueChange`로
 * 위임 — 서버 질의·debounce는 호출자 책임. 단일 텍스트 검색은 `SearchInput`을 쓴다. (RFC-filter-search-primitives)
 */
export const TokenSearchInput = forwardRef<HTMLInputElement, TokenSearchInputProps>(
  function TokenSearchInput(
    {
      inputValue,
      selectedTokens,
      options,
      onInputValueChange,
      onSelectedTokensChange,
      inputSize = "md",
      width,
      invalid = false,
      disabled = false,
      emptyMessage = defaultEmptyMessage,
      renderOnEmpty,
      renderResult,
      maxVisibleOptions = defaultMaxVisibleOptions,
      accessory,
      showClear = false,
      getOptionKeywords,
      id,
      className,
      placeholder,
      autoComplete = "off",
      autoCorrect = "off",
      autoCapitalize = "off",
      spellCheck = false,
      onFocus,
      onBlur,
      onKeyDown,
      ...inputProps
    },
    forwardedRef,
  ) {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const fallbackInputId = useId();
    const listboxId = useId();
    const { controlId, disabled: isDisabled } = useFieldControl({
      componentName: "TokenSearchInput",
      id,
      disabled,
    });
    const resolvedInputId = controlId ?? fallbackInputId;

    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const setInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const selectedValueSet = useMemo(
      () => new Set(selectedTokens.map((token) => token.value)),
      [selectedTokens],
    );
    const visibleOptions = useMemo(
      () =>
        filterOptions({
          options,
          excludeValues: selectedValueSet,
          query: inputValue,
          maxVisibleOptions,
          getOptionKeywords,
        }),
      [getOptionKeywords, inputValue, maxVisibleOptions, options, selectedValueSet],
    );

    useEffect(() => {
      if (isDisabled) {
        setOpen(false);
      }
    }, [isDisabled]);

    useEffect(() => {
      if (!open) {
        setActiveIndex(-1);
        return;
      }
      if (activeIndex >= visibleOptions.length) {
        setActiveIndex(-1);
      }
    }, [activeIndex, open, visibleOptions.length]);

    // Escape는 입력 onKeyDown에서 따로 처리하므로 hook의 Escape는 끈다.
    const dismissRefs = useMemo(() => [rootRef], []);
    const closeMenu = useCallback(() => setOpen(false), []);
    usePopoverDismiss({ open, refs: dismissRefs, onClose: closeMenu, closeOnEscape: false });

    const addOption = useCallback(
      (option: SearchOption, behavior: { blurInput?: boolean } = {}) => {
        if (isDisabled || option.disabled || selectedValueSet.has(option.value)) {
          return;
        }
        onSelectedTokensChange([...selectedTokens, toToken(option)]);
        onInputValueChange("");
        setOpen(false);
        setActiveIndex(-1);
        if (behavior.blurInput) {
          inputRef.current?.blur();
          return;
        }
        requestAnimationFrame(() => inputRef.current?.focus());
      },
      [isDisabled, onInputValueChange, onSelectedTokensChange, selectedTokens, selectedValueSet],
    );

    const removeToken = useCallback(
      (tokenValue: string) => {
        if (isDisabled) {
          return;
        }
        onSelectedTokensChange(selectedTokens.filter((token) => token.value !== tokenValue));
        requestAnimationFrame(() => inputRef.current?.focus());
      },
      [isDisabled, onSelectedTokensChange, selectedTokens],
    );

    const hasValue = inputValue !== "" || selectedTokens.length > 0;
    const handleClear = useCallback(() => {
      if (isDisabled) {
        return;
      }
      onInputValueChange("");
      onSelectedTokensChange([]);
      requestAnimationFrame(() => inputRef.current?.focus());
    }, [isDisabled, onInputValueChange, onSelectedTokensChange]);

    const handleInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onInputValueChange(event.target.value);
        setOpen(true);
        setActiveIndex(-1);
      },
      [onInputValueChange],
    );

    const handleInputFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        onFocus?.(event);
        if (!event.defaultPrevented) {
          setOpen(true);
        }
      },
      [onFocus],
    );

    const handleInputBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        onBlur?.(event);
      },
      [onBlur],
    );

    // 토큰이 여러 줄로 줄바꿈되면 컨트롤에 빈 공간(줄 끝·아래 여백·칩 라벨)이 생긴다. 그 빈 공간을
    // 클릭해도 입력에 포커스하고 캐럿을 맨 끝에 둔다. 인터랙티브 요소(칩 삭제·옵션·clear 버튼·입력
    // 자체)는 각자 동작을 유지하도록 제외한다.
    const handleControlMouseDown = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest("button, input, [role='option']")) {
        return;
      }
      event.preventDefault();
      const input = inputRef.current;
      if (!input) {
        return;
      }
      input.focus();
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, []);

    const handleInputKeyDown = useCallback(
      (event: ReactKeyboardEvent<HTMLInputElement>) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) {
          return;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          setOpen(true);
          setActiveIndex((current) => moveActiveIndex(current, visibleOptions, 1));
          return;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          setOpen(true);
          setActiveIndex((current) => moveActiveIndex(current, visibleOptions, -1));
          return;
        }

        if (event.key === "Escape") {
          setOpen(false);
          setActiveIndex(-1);
          return;
        }

        if (event.key === "Backspace" && inputValue === "" && selectedTokens.length > 0) {
          event.preventDefault();
          removeToken(selectedTokens[selectedTokens.length - 1].value);
          return;
        }

        if (event.key !== "Enter") {
          return;
        }
        if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
          return;
        }

        const activeOption = visibleOptions[activeIndex];
        if (open && activeOption !== undefined && !activeOption.disabled) {
          event.preventDefault();
          addOption(activeOption);
        }
      },
      [
        activeIndex,
        addOption,
        inputValue,
        onKeyDown,
        open,
        removeToken,
        selectedTokens,
        visibleOptions,
      ],
    );

    return (
      <Shell ref={rootRef} className={className} $width={width}>
        <Control
          $inputSize={inputSize}
          $invalid={invalid}
          $disabled={isDisabled}
          onMouseDown={handleControlMouseDown}
        >
          {selectedTokens.map((token) =>
            renderResult ? (
              <Fragment key={token.value}>
                {renderResult(token, {
                  remove: () => removeToken(token.value),
                  disabled: isDisabled,
                })}
              </Fragment>
            ) : (
              <Token key={token.value} $accentColor={token.accentColor} title={token.description}>
                <TokenLabel>{token.label}</TokenLabel>
                <RemoveButton
                  type="button"
                  disabled={isDisabled}
                  aria-label={`Remove ${token.label}`}
                  onClick={() => removeToken(token.value)}
                >
                  <CloseIcon width="0.7em" height="0.7em" aria-hidden />
                </RemoveButton>
              </Token>
            ),
          )}
          <Input
            ref={setInputRef}
            id={resolvedInputId}
            value={inputValue}
            disabled={isDisabled}
            aria-invalid={invalid || undefined}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            role="combobox"
            placeholder={selectedTokens.length === 0 ? placeholder : undefined}
            autoComplete={autoComplete}
            autoCorrect={autoCorrect}
            autoCapitalize={autoCapitalize}
            spellCheck={spellCheck}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            {...inputProps}
          />

          {accessory || (showClear && hasValue) ? (
            <Accessories>
              {accessory}
              {showClear && hasValue ? (
                <IconButton
                  aria-label="Clear"
                  size="xs"
                  variant="ghost"
                  disabled={isDisabled}
                  onClick={handleClear}
                >
                  <CloseIcon width="0.72em" height="0.72em" />
                </IconButton>
              ) : null}
            </Accessories>
          ) : null}

          {open && !isDisabled ? (
            <Menu id={listboxId} role="listbox">
              {visibleOptions.length === 0 ? (
                renderOnEmpty ? (
                  renderOnEmpty(inputValue)
                ) : (
                  <EmptyText>{emptyMessage}</EmptyText>
                )
              ) : (
                visibleOptions.map((option, index) => (
                  <Option
                    key={option.value}
                    type="button"
                    $active={index === activeIndex}
                    role="option"
                    disabled={option.disabled}
                    aria-selected={index === activeIndex}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => addOption(option, { blurInput: true })}
                  >
                    <OptionMain>
                      <OptionLabel $accentColor={option.accentColor}>{option.label}</OptionLabel>
                      {option.description ? (
                        <OptionDescription>{option.description}</OptionDescription>
                      ) : null}
                    </OptionMain>
                  </Option>
                ))
              )}
            </Menu>
          ) : null}
        </Control>
      </Shell>
    );
  },
);

TokenSearchInput.displayName = "TokenSearchInput";

function toToken(option: SearchOption): SearchToken {
  return {
    value: option.value,
    label: option.label,
    description: option.description,
    accentColor: option.accentColor,
  };
}
